import { Injectable } from "@angular/core";
import { BehaviorSubject, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { SchemaService } from "../../../project/project-schema/shared/services/schema.service";
import { SchemaRouteStationInfo } from "../../../project/project-schema/shared/interfaces/schema-route-station-info.interface";
import { ProjectRouteStation } from "../../../project/shared/interfaces/project-route-station.interface";
import { DeviceService } from "./device.service";
import { DeviceDataSessionStorageService } from "./device-data-session-storage.service";
import { DataEndChannel } from "../../../project/shared/interfaces/data-end-channel.interface";
import { RouteStationService } from "../../../project/shared/services/route-station.service";
import { DataEndChannelError } from "../../../project/shared/interfaces/data-end-channel-error.interface";
import { ProjectRouteStationWithParam } from "../../../project/shared/interfaces/project-route-station.class";

@Injectable({
    providedIn: 'root'
})
export class DeviceDataService {
    private routeStationsSubject$ = new BehaviorSubject<ProjectRouteStation[]>([]);
    public routeStations = this.routeStationsSubject$.asObservable();
    private activeRouteStationSubject$ = new BehaviorSubject<ProjectRouteStation | undefined>(undefined);
    public activeRouteStation = this.activeRouteStationSubject$.asObservable();
    private dataEndChannelDeviceSubject$ = new BehaviorSubject<DataEndChannel[]>([]);
    public dataEndChannelDevice = this.dataEndChannelDeviceSubject$.asObservable();
    private dataEndChannelErrorsSubject$ = new BehaviorSubject<DataEndChannelError[]>([]);
    public dataEndChannelErrors = this.dataEndChannelErrorsSubject$.asObservable();
    private routeStationWithDeviceParamSubject$ = new BehaviorSubject<ProjectRouteStationWithParam | undefined>(undefined);
    public routeStationWithDeviceParam = this.routeStationWithDeviceParamSubject$.asObservable();

    constructor(private readonly deviceService: DeviceService,
        private readonly deviceSessionService: DeviceDataSessionStorageService,
        private readonly stationService: RouteStationService,
        private readonly schemaService: SchemaService) { }

    public updateRouteStations(routeStations: ProjectRouteStation[]): void {
        this.routeStationsSubject$.next(routeStations);
        if (this.deviceSessionService.getActiveRouteStation()) {
            this.updateActiveRouteStation(routeStations.find(
                r => r.id === this.deviceSessionService.getActiveRouteStation()
            ));
        }
    }

    public updateActiveRouteStation(activeRouteStation: ProjectRouteStation | undefined): void {
        this.activeRouteStationSubject$.next(activeRouteStation);
        if (activeRouteStation) {
            this.getSchemaInfo(activeRouteStation)
            this.deviceSessionService.setActiveRouteStation(activeRouteStation.id);
            this.getDataEndChannels(activeRouteStation);
            this.getDataEndChannelErrors(activeRouteStation.samId);
        } else {
            this.updateDataEndChannelDevice([]);
            this.deviceSessionService.deleteActiveRouteStation();
            this.updateRouteStationWithParam(undefined);
        }
    }

    public updateDataEndChannelDevice(dataEndChannels: DataEndChannel[]): void {
        this.dataEndChannelDeviceSubject$.next(dataEndChannels);
    }

    public updateDataEndChannelErrorDevice(dataEndChannelErrors: DataEndChannelError[]): void {
        this.dataEndChannelErrorsSubject$.next(dataEndChannelErrors);
    }

    private getSchemaInfo(activeRouteStation: ProjectRouteStation): void {
        // Anzeige-Sicht für die Stationskachel; Fehler dürfen die Geräteansicht nicht blockieren
        this.schemaService.getSchemaDeviceInfo(activeRouteStation.id)
            .pipe(catchError(() => of(undefined as SchemaRouteStationInfo | undefined)))
            .subscribe(schemaInfo => {
                this.updateRouteStationWithParam(new ProjectRouteStationWithParam(activeRouteStation, schemaInfo));
            });
    }

    private getDataEndChannels(activeRouteStation: ProjectRouteStation): void {
        this.stationService.getDataEndChannels(activeRouteStation.id).subscribe(dataEndChannels => {
            if (!dataEndChannels) { dataEndChannels = []; }
            else activeRouteStation.dataEndChannels = dataEndChannels;
            this.updateDataEndChannelDevice(dataEndChannels);
        });
    }

    private getDataEndChannelErrors(samId: number): void {
        this.deviceService.getChannelErrors(samId).subscribe(channelErrors => {
            this.updateDataEndChannelErrorDevice(channelErrors);
        });
    }

    private updateRouteStationWithParam(routeStationWithParam: ProjectRouteStationWithParam | undefined): void {
        this.routeStationWithDeviceParamSubject$.next(routeStationWithParam);
    }
}